import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, TouchableOpacity } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import ChevronRight from '@/assets/icons/chevronRight';
import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { TraditionalMatchupCard } from '@/components/sbk-traditional-matchup-card/TraditionalMatchupCard';
import { EventWithMarkets } from '@/feature/event-details-sbk/types';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { SweepsLobbyTabNavigationProp } from '@/navigation/sweeps/types';

import { useLeagueTraditionalMatchupDataWithMarkets } from '../hooks/use-league-traditional-matchup-data';
import { useSbkLobbySectionSelection } from '../hooks/use-sbk-lobby-section-selection';
import { EventListSkeleton } from './EventListSkeleton';

const IMAGE_SIZE = 28;
const DEFAULT_EVENT_LIMIT = 5;

export type FeaturedLeague = {
    id: string;
    name: string;
    imageUrl?: string;
    eventCount?: number;
};

type FeaturedLeagueProps = {
    featuredLeague: FeaturedLeague;
    marketCode?: string;
};

const keyExtractor = (item: EventWithMarkets) => item.id;

export const FeaturedLeagueSection = ({
    featuredLeague: { id: leagueId, name, imageUrl, eventCount = DEFAULT_EVENT_LIMIT },
}: FeaturedLeagueProps) => {
    const { t } = useTranslation('lobby_sbk');
    const navigation = useNavigation<SweepsLobbyTabNavigationProp>();
    const { events, hasAnyAvailableMarkets, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
        useLeagueTraditionalMatchupDataWithMarkets({
            leagueId,
            limit: eventCount,
        });
    const setSelectedTile = useSbkLobbySectionSelection(state => state.actions.setSelectedTile);

    const navigateToEvent = useCallback(
        (event: EventWithMarkets, eventIndex: number) => {
            useEventsTabsStore.getState().actions.setSelectedTab(eventIndex);
            navigation.navigate('SbkEventsSheets', {
                eventId: event.id,
                contextKey: new Date().getTime().toString(),
                leagueId: event?.league?.id,
            });
        },
        [navigation]
    );

    const renderItem = useCallback(
        ({ item, index }: { item: EventWithMarkets; index: number }) => {
            return (
                <TraditionalMatchupCard
                    event={item}
                    showHeader={index === 0}
                    isLastItem={index === events.length - 1}
                    onPress={() => navigateToEvent(item, index)}
                />
            );
        },
        [events.length, navigateToEvent]
    );

    if (!isLoading && !hasAnyAvailableMarkets) {
        return null; // Hide the section if there aren't any available markets
    }

    return (
        <Box>
            <TouchableOpacity
                onPress={() => {
                    setSelectedTile(leagueId);
                    navigation.setParams({
                        leagueId,
                    });
                }}
            >
                <Row px="s16" mb="s20" mt="s16">
                    {imageUrl ? (
                        <Image
                            source={{ uri: imageUrl }}
                            resizeMode="contain"
                            style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}
                        />
                    ) : null}
                    <Box flexShrink={1}>
                        <Text variant="headlineMedium" marginLeft="s12" lineHeight={28} mr="s14" numberOfLines={1}>
                            {name}
                        </Text>
                    </Box>
                    <Box
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                        width={28}
                        height={28}
                        borderRadius="r40"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <ChevronRight />
                    </Box>
                </Row>
            </TouchableOpacity>
            {isLoading ? (
                <EventListSkeleton leagueId={leagueId} count={1} />
            ) : (
                <FlatList
                    data={events}
                    renderItem={renderItem}
                    keyExtractor={keyExtractor}
                    scrollEnabled={false}
                    ListFooterComponent={
                        isFetchingNextPage ? (
                            <>
                                <Box mb="s20">
                                    <LineSeparator />
                                </Box>
                                <EventListSkeleton leagueId={leagueId} count={eventCount} hideHeader />
                            </>
                        ) : hasNextPage ? (
                            <Box
                                px="s16"
                                pb="s24"
                                mb="s8"
                                borderBottomWidth={1}
                                borderColor={ds.colorToken.border.strong}
                                mt="s4"
                            >
                                <Button style={styles.button} label={t('view_more')} onPress={() => fetchNextPage()} />
                            </Box>
                        ) : (
                            <Box mb="s8">
                                <LineSeparator />
                            </Box>
                        )
                    }
                />
            )}
        </Box>
    );
};

const styles = StyleSheet.create({
    button: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
});
